// Экраны «Страница не найдена» и «Не удалось загрузить» (contracts/ui.md; ux.md, раздел 14):
// что случилось → что с данными → путь вперёд, код ошибки и номер операции без технических
// подробностей (FR-028). Внутри каркаса это страница (README «Каркас экрана»): заголовок
// page-title и одна строка note, ниже — EmptyState со значком, кодом и действиями. Без каркаса —
// экран на белом листе (kp-screen: color-surface, radius-sheet, рамка color-line).
import { EmptyState, Button, PageHeader, buttonClass } from '@kp/ui';
import type { LucideIcon } from '@kp/ui';
import { CircleAlert, FileQuestion } from '@kp/ui/icons';
import { Link, useRouter } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useInShell } from '../shell/context.tsx';
import { errorView } from './errors.ts';

export interface PageProblemProps {
  /** Заголовок страницы: «Нет доступа», «Страница не найдена», «Не удалось загрузить». */
  title: string;
  /** Одна строка пояснения: причина и путь вперёд. */
  note: ReactNode;
  icon: LucideIcon;
  /** Ошибка: значок color-bad, экран озвучивается сразу (role="alert"). */
  error?: boolean;
  code?: string;
  operation?: string;
  /** Путь вперёд: «На главную», «Повторить». */
  action?: ReactNode;
}

/**
 * Отказ, «не найдено» или ошибка внутри каркаса — страница: заголовок page-title и строка note
 * (README «Каркас экрана»), под ними — значок, код с номером операции и действия (EmptyState).
 */
export function PageProblem({
  title,
  note,
  icon,
  error = false,
  code,
  operation,
  action,
}: PageProblemProps) {
  const body = (
    <>
      <PageHeader title={title} note={note} />
      <EmptyState
        icon={icon}
        code={code}
        operation={operation}
        action={action}
        // Значок ошибки — без второго role="alert": озвучивается весь экран.
        className={error ? 'err' : undefined}
      />
    </>
  );
  return (
    <main id="main" tabIndex={-1} className="kp-page">
      {error ? (
        <div role="alert" className="kp-problem">
          {body}
        </div>
      ) : (
        body
      )}
    </main>
  );
}

const NOT_FOUND_TITLE = 'Страница не найдена';
const NOT_FOUND_TEXT = 'Её перенесли или удалили. Проверьте ссылку';

export function NotFoundScreen() {
  const router = useRouter();
  const inShell = useInShell();
  const action = (
    <>
      <Button onClick={() => router.history.back()}>Назад</Button>
      <Link to="/" className={buttonClass({ variant: 'primary' })}>
        На главную
      </Link>
    </>
  );
  if (inShell) {
    return (
      <PageProblem
        title={NOT_FOUND_TITLE}
        note={NOT_FOUND_TEXT}
        icon={FileQuestion}
        action={action}
      />
    );
  }
  return (
    <main id="main" tabIndex={-1} className="kp-screen">
      <EmptyState
        icon={FileQuestion}
        headingLevel={1}
        title={NOT_FOUND_TITLE}
        text={NOT_FOUND_TEXT}
        action={action}
      />
    </main>
  );
}

export function ErrorScreen({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const inShell = useInShell();
  // Обрыв связи — NET-001, ответ сервера — его код и номер операции, остальное — SYS-001
  // (contracts/errors.md).
  const view = errorView(error);
  const action = (
    <>
      <Link to="/" className={buttonClass({ variant: 'secondary' })}>
        На главную
      </Link>
      <Button
        variant="primary"
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        Повторить
      </Button>
    </>
  );
  if (inShell) {
    return (
      <PageProblem
        error
        title="Не удалось загрузить"
        note="Данные в сохранности."
        icon={CircleAlert}
        code={view.code}
        operation={view.operation}
        action={action}
      />
    );
  }
  return (
    <main id="main" tabIndex={-1} className="kp-screen">
      <EmptyState
        error
        icon={CircleAlert}
        headingLevel={1}
        title="Не удалось загрузить"
        text="Данные в сохранности."
        code={view.code}
        operation={view.operation}
        action={action}
      />
    </main>
  );
}
